import type {
  BotSkill,
  FriendView,
  RoomConfig,
  RoomCrest,
  RoomView,
  SeatView,
  SessionFormat,
  TurnTimer,
} from '@rummy/protocol';
import { decksForSeats } from '@rummy/engine';
import { useMemo, useRef, useState } from 'react';
import { Portrait } from '../components/Portrait.tsx';
import { RulePrimer } from '../components/RulePrimer.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { BOT_CHARACTER_KEYS } from '../components/Portrait.tsx';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import type { GameClient } from '../net/client.ts';
import { encodeQr, qrPath } from '../util/qr.ts';

const TIMERS: TurnTimer[] = [30, 45, 60];
const DEALS: SessionFormat[] = ['deals-2', 'deals-3', 'deals-6'];
const POOLS: SessionFormat[] = ['pool-101', 'pool-201'];
const MIN_SEATS = 2;
const MAX_SEATS = 6;
const BOT_NAMES: Record<string, string> = {
  nila: 'Nila',
  arun: 'Arun',
  devi: 'Devi',
  kabir: 'Kabir',
  meera: 'Meera',
  ravi: 'Ravi',
  sana: 'Sana',
  jai: 'Jai',
  ila: 'Ila',
  tara: 'Tara',
};
/** The three the lobby offers, plus the personalities worth choosing by hand. */
const SKILLS: BotSkill[] = [
  'casual', 'sharp', 'expert', 'patient', 'opportunist', 'keeper', 'low-card', 'tidy',
];
const FILL_SKILLS: BotSkill[] = ['casual', 'sharp', 'expert'];
const CRESTS: { key: RoomCrest; glyph: string }[] = [
  { key: 'spade', glyph: '♠' },
  { key: 'lotus', glyph: '✦' },
  { key: 'sun', glyph: '☀' },
  { key: 'moon', glyph: '☾' },
];

export interface RoomScreenProps {
  room: RoomView;
  friends: FriendView[];
  accountId: string;
  client: GameClient;
  onLeave: () => void;
}

/**
 * §4.3 / §6.1 screen 3.
 *
 * The room is not a settings form with a seat list bolted on; it is the table,
 * shown before anyone sits at it. Seats are arranged the way they will be
 * arranged in the game — you at the bottom, everyone else around — with the
 * deck in the middle, so the screen previews the thing it is about to start.
 *
 * "Previews" is meant literally, and used not to be: the table rendered a flat
 * disc while the game rendered a lit, skinned surface, so choosing a table skin
 * showed you nothing until after the deal. The tableau now paints the same
 * skin, under a rail, with the room's own crest pressed into the felt.
 *
 * Two decisions live on the table itself rather than in a settings drawer,
 * because they are decisions about the table: how many chairs there are, and
 * who is sitting in them.
 */
export function RoomScreen({ room, friends, accountId, client, onLeave }: RoomScreenProps) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [picking, setPicking] = useState(false);
  const [seatMenu, setSeatMenu] = useState(false);
  const [crestOpen, setCrestOpen] = useState(false);
  const [skillFor, setSkillFor] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [primerOpen, setPrimerOpen] = useState(false);
  const [primerStep, setPrimerStep] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'invite' | 'format' | 'rules' | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);

  const isHost = room.hostId === accountId;
  const canStart = room.seats.length >= 2;
  const empties = Math.max(0, room.config.seats - room.seats.length);
  const set = (patch: Partial<RoomConfig>) => client.configureRoom(patch);
  const taken = new Set(room.seats.map((s) => s.character).filter(Boolean));
  const onlineFriends = friends.filter((f) => f.presence === 'online');
  const family: 'deals' | 'pool' = room.config.format.startsWith('deals') ? 'deals' : 'pool';
  const named = room.label !== room.code;
  const host = room.seats.find((seat) => seat.id === room.hostId);
  const crestGlyph = CRESTS.find((crest) => crest.key === room.crest)?.glyph ?? '♠';
  // Asked of the engine rather than restated here: this label and the deal it
  // describes must never be able to disagree.
  const decks = decksForSeats(room.config.seats);
  const skillSeat = room.seats.find((seat) => seat.id === skillFor) ?? null;

  const qr = useMemo(() => {
    const code = encodeQr(room.inviteUrl);
    return { size: code.size, path: qrPath(code) };
  }, [room.inviteUrl]);

  /**
   * Seats laid out around an ellipse: you at the bottom, the rest around the
   * arc, exactly where they will be during the hand.
   */
  const placed = useMemo(() => {
    const mine = room.seats.filter((s) => s.id === accountId);
    const others = room.seats.filter((s) => s.id !== accountId);
    const filled: (SeatView | null)[] = [...mine, ...others];
    while (filled.length < room.config.seats) filled.push(null);
    const n = filled.length;
    return filled.map((seat, i) => {
      const angle = (Math.PI / 2) + (i * 2 * Math.PI) / n;
      return {
        seat,
        key: seat?.id ?? `empty-${i}`,
        left: 50 + 39 * Math.cos(angle),
        top: 50 + 37 * Math.sin(angle),
      };
    });
  }, [room.seats, room.config.seats, accountId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(room.inviteUrl);
    } catch {
      /* clipboard denied — the URL is on screen either way */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const closeMenus = () => {
    setSeatMenu(false);
    setPicking(false);
    setCrestOpen(false);
    setSkillFor(null);
  };

  return (
    <main className="room" data-mobile-panel={mobilePanel ?? 'none'}>
      <header className="room__masthead">
        <div className="room__identity">
          <div className="room__eyebrow-row">
            <span className={`room__crest room__crest--${room.crest}`} aria-hidden="true">
              {crestGlyph}
            </span>
            <p className="room__eyebrow">
              {t('room.eyebrow')} · {host?.handle ?? t('room.host')}
            </p>
          </div>
          {renaming ? (
            <form
              className="room__rename"
              onSubmit={(e) => {
                e.preventDefault();
                client.renameRoom(nameInput.current?.value ?? '');
                setRenaming(false);
              }}
            >
              <input
                ref={nameInput}
                className="room__name-input"
                defaultValue={named ? room.label : ''}
                placeholder={t('room.namePlaceholder')}
                maxLength={40}
                autoFocus
                onBlur={() => setRenaming(false)}
              />
            </form>
          ) : (
            <h1 className="room__name">
              {named ? room.label : t('room.untitled')}
              {isHost && (
                <button
                  type="button"
                  className="room__rename-btn"
                  onClick={() => setRenaming(true)}
                  aria-label={t('room.rename')}
                >
                  ✎
                </button>
              )}
            </h1>
          )}
          {/* The generated id is how people say the room aloud, so it stays —
              as a chip, not as the largest type on the page. */}
          <p className="room__meta">
            <code className="room__code">{room.code}</code>
            <span>
              {t(`format.${room.config.format}` as MessageKey)} ·{' '}
              {t('room.decks.short', { decks })}
            </span>
          </p>
          {/* The crest picker used to be four permanent buttons here for a
              decision made once. It now hangs off the crest on the felt. */}
        </div>
        <button type="button" className="btn btn--ghost" onClick={onLeave}>
          {t('room.leave')}
        </button>
      </header>

      {/* Phone room setup is a board with three focused sheets, rather than a
          long configuration page. The primary Start action remains visible
          underneath whichever detail is being edited. */}
      <nav className="room__mobile-actions" aria-label={t('settings.heading')}>
        <button type="button" className="btn btn--small" onClick={() => setMobilePanel('invite')}>
          {t('room.invite')}
        </button>
        <button type="button" className="btn btn--small" onClick={() => setMobilePanel('format')}>
          {t('room.format')}
        </button>
        <button type="button" className="btn btn--small" onClick={() => setMobilePanel('rules')}>
          {t('room.rules')}
        </button>
      </nav>

      <div className="room__grid">
        {/* ------------------------------------------------------- the table */}
        <section className="panel panel--felt">
          <div className="tableau">
            {/* Rail, felt and light are three stacked ellipses rather than one
                disc, so the felt sits *down* inside the table. All CSS: there is
                no image to fetch, mis-encode or fail to load. */}
            <div className="tableau__rail" aria-hidden="true" />
            <div className="tableau__felt" aria-hidden="true" />
            <div className="tableau__glow" aria-hidden="true" />

            {isHost ? (
              <button
                type="button"
                className="tableau__crest tableau__crest--button"
                onClick={() => {
                  closeMenus();
                  setCrestOpen(true);
                }}
                aria-label={t('room.crest')}
                aria-expanded={crestOpen}
              >
                {crestGlyph}
              </button>
            ) : (
              <span className="tableau__crest" aria-hidden="true">
                {crestGlyph}
              </span>
            )}

            <div className="tableau__centre" aria-hidden="true">
              <span className="tableau__deck tableau__deck--3" />
              <span className="tableau__deck tableau__deck--2" />
              <span className="tableau__deck" />
              {/* The cut card, face up beside the stack, exactly as it will be
                  dealt — the table's one splash of white. */}
              <span className="tableau__cut">{crestGlyph}</span>
            </div>

            <p className="tableau__count">
              {t('lobby.seatCount', {
                seated: room.seats.length,
                total: room.config.seats,
              })}
            </p>

            <ul className="tableau__seats">
              {placed.map(({ seat, key, left, top }, index) => (
                <li
                  key={key}
                  className={`tableau__seat${seat ? '' : ' is-empty'}`}
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    animationDelay: `${index * 45}ms`,
                  }}
                >
                  {seat ? (
                    <>
                      <span className="tableau__ring">
                        <Portrait
                          character={seat.character ?? seat.avatar}
                          handle={seat.handle}
                          size={54}
                        />
                      </span>
                      <span className="tableau__plate">
                        <span className="tableau__name">{seat.handle}</span>
                        <span className="tableau__tags">
                          {seat.isHost && <span className="tag">{t('room.host')}</span>}
                          {seat.id === accountId && (
                            <span className="tag tag--quiet">{t('room.you')}</span>
                          )}
                          {seat.isBot &&
                            (isHost ? (
                              /* A practice opponent's policy is a real choice
                                 the engine already supports eight of, so it is
                                 a control rather than a label. */
                              <button
                                type="button"
                                className="tag tag--quiet tag--button"
                                onClick={() => {
                                  closeMenus();
                                  setSkillFor(seat.id);
                                }}
                                aria-label={t('room.seat.styleFor', { name: seat.handle })}
                              >
                                {seat.skill
                                  ? t(`bot.skill.${seat.skill}` as MessageKey)
                                  : t('room.bot')}
                              </button>
                            ) : (
                              <span className="tag tag--quiet">{t('room.bot')}</span>
                            ))}
                        </span>
                      </span>
                      {isHost && seat.id !== accountId && (
                        <button
                          type="button"
                          className="tableau__remove"
                          onClick={() => client.removeSeat(seat.id)}
                          aria-label={`${t('room.remove')} ${seat.handle}`}
                        >
                          ×
                        </button>
                      )}
                    </>
                  ) : (
                    /* An open seat is the invitation, not a label about one. */
                    <button
                      type="button"
                      className="tableau__open"
                      onClick={() => {
                        closeMenus();
                        setSeatMenu(true);
                      }}
                      disabled={!isHost && empties === 0}
                    >
                      <span className="tableau__open-ring" aria-hidden="true">
                        +
                      </span>
                      <span className="tableau__open-label">{t('room.emptySeat')}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/*
            Seat count and filling the table are decisions *about the table*, so
            they sit on it. Seats used to live two clicks deep inside a collapsed
            rules drawer, below the turn timer — the single most consequential
            setting on the page, folded away.
          */}
          {isHost && (
            <div className="table-bar">
              <div className="table-bar__seats" role="group" aria-label={t('room.seats')}>
                <button
                  type="button"
                  className="table-bar__step"
                  onClick={() => set({ seats: room.config.seats - 1 })}
                  disabled={room.config.seats <= Math.max(MIN_SEATS, room.seats.length)}
                  aria-label={t('room.seats.remove')}
                >
                  −
                </button>
                <span className="table-bar__count">
                  {t('room.seats.count', { seats: room.config.seats })}
                </span>
                <button
                  type="button"
                  className="table-bar__step"
                  onClick={() => set({ seats: room.config.seats + 1 })}
                  disabled={room.config.seats >= MAX_SEATS}
                  aria-label={t('room.seats.add')}
                >
                  +
                </button>
              </div>
              {empties > 0 && (
                <button
                  type="button"
                  className="btn btn--small table-bar__fill"
                  onClick={() => client.fillSeats(room.config.fillSkill)}
                  title={t('room.fillTable.note', { count: empties })}
                >
                  {t('room.fillTable')}
                </button>
              )}
            </div>
          )}

          {crestOpen && (
            <div className="seat-menu" role="dialog" aria-label={t('room.crest')}>
              <p className="seat-menu__title">{t('room.crest')}</p>
              <div className="room__crests" role="group" aria-label={t('room.crest')}>
                {CRESTS.map(({ key, glyph }) => (
                  <button
                    key={key}
                    type="button"
                    className={`room__crest-choice${room.crest === key ? ' is-on' : ''}`}
                    aria-pressed={room.crest === key}
                    aria-label={t(`room.crest.${key}` as MessageKey)}
                    onClick={() => {
                      client.setRoomCrest(key);
                      setCrestOpen(false);
                    }}
                  >
                    {glyph}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setCrestOpen(false)}
              >
                {t('settings.close')}
              </button>
            </div>
          )}

          {skillSeat && (
            <div className="seat-menu" role="dialog" aria-label={t('room.seat.style')}>
              <p className="seat-menu__title">
                {t('room.seat.styleFor', { name: skillSeat.handle })}
              </p>
              <ul className="skill-picker">
                {SKILLS.map((skill) => (
                  <li key={skill}>
                    <button
                      type="button"
                      className={`option option--compact${skillSeat.skill === skill ? ' is-on' : ''}`}
                      aria-pressed={skillSeat.skill === skill}
                      onClick={() => {
                        client.setBotSkill(skillSeat.id, skill);
                        setSkillFor(null);
                      }}
                    >
                      <span className="option__label">
                        {t(`bot.skill.${skill}` as MessageKey)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setSkillFor(null)}
              >
                {t('settings.close')}
              </button>
            </div>
          )}

          {seatMenu && (
            <div className="seat-menu" role="dialog" aria-label={t('room.fillSeat')}>
              {picking ? (
                <>
                  <p className="seat-menu__title">{t('room.chooseOpponent')}</p>
                  <ul className="bot-picker__list">
                    {BOT_CHARACTER_KEYS.filter((c) => !taken.has(c)).map((c) => (
                      <li key={c}>
                        <button
                          type="button"
                          className="bot-picker__option"
                          onClick={() => {
                            client.addBot(c, room.config.fillSkill);
                            setPicking(false);
                            setSeatMenu(false);
                          }}
                        >
                          <Portrait character={c} handle={BOT_NAMES[c] ?? c} size={50} />
                          <span className="bot-picker__name">{BOT_NAMES[c] ?? c}</span>
                          <span className="bot-picker__trait">
                            {t(`bot.trait.${c}` as MessageKey)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="seat-menu__title">{t('room.fillSeat')}</p>
                  <div className="seat-menu__actions">
                    <button
                      type="button"
                      className={`btn btn--primary${copied ? ' btn--done' : ''}`}
                      onClick={copyLink}
                    >
                      {copied ? `✓ ${t('room.copied')}` : t('room.seatMenu.invite')}
                    </button>
                    {isHost && (
                      <>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => {
                            client.addBot(undefined, room.config.fillSkill);
                            setSeatMenu(false);
                          }}
                        >
                          {t('room.addBot')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => setPicking(true)}
                        >
                          {t('room.chooseOpponent')}
                        </button>
                        {/* Closing a seat is the other half of the ± on the
                            bar, reached from the seat it applies to. */}
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => {
                            set({ seats: room.config.seats - 1 });
                            setSeatMenu(false);
                          }}
                          disabled={room.config.seats <= Math.max(MIN_SEATS, room.seats.length)}
                        >
                          {t('room.closeSeat')}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => {
                  setSeatMenu(false);
                  setPicking(false);
                }}
              >
                {t('settings.close')}
              </button>
            </div>
          )}
        </section>

        {/* -------------------------------------------------------- the side */}
        <div className="room__side">
          <div className="room__mobile-sheet-head">
            <button type="button" className="btn btn--small btn--ghost" onClick={() => setMobilePanel(null)}>
              {t('settings.close')}
            </button>
          </div>
          <section className="panel panel--invite room__mobile-panel room__mobile-panel--invite">
            <div className="panel__head">
              <h2 className="panel__title">{t('room.invite')}</h2>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setShowQr((v) => !v)}
                aria-expanded={showQr}
              >
                {showQr ? t('room.qr.hide') : t('room.qr')}
              </button>
            </div>
            <div className="invite">
              <code className="invite__url">{room.inviteUrl}</code>
              <button
                type="button"
                className={`btn btn--primary${copied ? ' btn--done' : ''}`}
                onClick={copyLink}
              >
                {copied ? `✓ ${t('room.copied')}` : t('room.copy')}
              </button>
            </div>
            {showQr && (
              <div className="qr">
                {/* One path, no dependency, verified against a reference
                    encoder in the test suite. */}
                <svg
                  className="qr__code"
                  viewBox={`-2 -2 ${qr.size + 4} ${qr.size + 4}`}
                  role="img"
                  aria-label={t('room.qr.alt')}
                >
                  <rect x={-2} y={-2} width={qr.size + 4} height={qr.size + 4} className="qr__quiet" />
                  <path d={qr.path} className="qr__dark" />
                </svg>
                <p className="muted muted--small">{t('room.qr.note')}</p>
              </div>
            )}
            {onlineFriends.length > 0 && (
              <ul className="friends friends--compact">
                {onlineFriends.map((friend) => (
                  <li key={friend.id} className="friends__row">
                    <Portrait character={friend.avatar} handle={friend.handle} size={30} />
                    <span className="friends__name">{friend.handle}</span>
                    <button
                      type="button"
                      className="btn btn--small"
                      onClick={() => client.invite(friend.id)}
                    >
                      {t('lobby.invite')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel room__mobile-panel room__mobile-panel--format">
            <h2 className="panel__title">{t('room.format')}</h2>
            {/* Deals and Pool are different games; the family comes first. */}
            <Segmented
              label={t('room.format')}
              value={family}
              disabled={!isHost}
              options={[
                { value: 'deals', label: t('format.family.deals') },
                { value: 'pool', label: t('format.family.pool') },
              ]}
              onChange={(next) =>
                set({ format: next === 'deals' ? 'deals-3' : 'pool-101' })
              }
            />
            <ul className="option-list">
              {(family === 'deals' ? DEALS : POOLS).map((format) => (
                <li key={format}>
                  <button
                    type="button"
                    className={`option${room.config.format === format ? ' is-on' : ''}`}
                    disabled={!isHost}
                    aria-pressed={room.config.format === format}
                    onClick={() => set({ format })}
                  >
                    <span className="option__label">{t(`format.${format}` as MessageKey)}</span>
                    <span className="option__note">
                      {t(`format.note.${format}` as MessageKey)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* A host setting up Thursday's game touches format and seats, and
              seats now live on the table. The rest is folded away behind what it
              currently says. */}
          <section className="panel panel--rules room__mobile-panel room__mobile-panel--rules">
            <button
              type="button"
              className="rules__toggle"
              onClick={() => setRulesOpen((v) => !v)}
              aria-expanded={rulesOpen}
            >
              <span className="panel__title">{t('room.rules')}</span>
              <span className="rules__summary">
                {t('room.rules.summary', {
                  seats: room.config.seats,
                  seconds: room.config.turnTimer,
                  guard: room.config.declarationGuard ? 'on' : 'off',
                })}
              </span>
              <span className={`rules__chevron${rulesOpen ? ' is-open' : ''}`} aria-hidden="true">
                ▾
              </span>
            </button>

            {rulesOpen && (
              <div className="rules__body">
                <p className="muted muted--small">{t('room.decks')}</p>

                <h3 className="field__label">{t('room.turnTimer')}</h3>
                <Segmented
                  label={t('room.turnTimer')}
                  value={room.config.turnTimer}
                  disabled={!isHost}
                  options={TIMERS.map((seconds) => ({
                    value: seconds,
                    label: t('room.turnTimer.short', { seconds }),
                  }))}
                  onChange={(turnTimer) => set({ turnTimer })}
                />

                <h3 className="field__label">{t('room.fillSkill')}</h3>
                <Segmented
                  even
                  label={t('room.fillSkill')}
                  value={room.config.fillSkill}
                  disabled={!isHost}
                  options={FILL_SKILLS.map((skill) => ({
                    value: skill,
                    label: t(`bot.skill.${skill}` as MessageKey),
                  }))}
                  onChange={(fillSkill) => set({ fillSkill })}
                />
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={room.config.autoFillBots}
                    disabled={!isHost}
                    onChange={(e) => set({ autoFillBots: e.target.checked })}
                  />
                  <span className="switch__track" aria-hidden="true">
                    <span className="switch__thumb" />
                  </span>
                  <span className="switch__label">{t('room.autoFill')}</span>
                </label>
                <p className="muted muted--small">{t('room.autoFill.help')}</p>

                <h3 className="field__label">{t('room.guard')}</h3>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={room.config.declarationGuard}
                    disabled={!isHost}
                    onChange={(e) => set({ declarationGuard: e.target.checked })}
                  />
                  <span className="switch__track" aria-hidden="true">
                    <span className="switch__thumb" />
                  </span>
                  <span className="switch__label">
                    {room.config.declarationGuard ? t('room.guard.on') : t('room.guard.off')}
                  </span>
                </label>
                {/* Not silent, not invisible: every player sees it (§6.4). */}
                <p className="muted muted--small">{t('room.guard.help')}</p>

                {/* §3.8. Only pool 201 has a re-entry to offer, and a table
                    that does not want one should not have to discover it
                    mid-session when a score jumps backwards. */}
                {room.config.format === 'pool-201' && (
                  <>
                    <h3 className="field__label">{t('room.reentry')}</h3>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={room.config.reentry}
                        disabled={!isHost}
                        onChange={(e) => set({ reentry: e.target.checked })}
                      />
                      <span className="switch__track" aria-hidden="true"><span className="switch__thumb" /></span>
                      <span className="switch__label">
                        {room.config.reentry ? t('room.reentry.on') : t('room.reentry.off')}
                      </span>
                    </label>
                    <p className="muted muted--small">{t('room.reentry.help')}</p>
                  </>
                )}

                <h3 className="field__label">{t('room.hostControls')}</h3>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={room.config.seatLock}
                    disabled={!isHost}
                    onChange={(e) => set({ seatLock: e.target.checked })}
                  />
                  <span className="switch__track" aria-hidden="true"><span className="switch__thumb" /></span>
                  <span className="switch__label">{t('room.seatLock')}</span>
                </label>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={room.config.pauseBetweenHands}
                    disabled={!isHost}
                    onChange={(e) => set({ pauseBetweenHands: e.target.checked })}
                  />
                  <span className="switch__track" aria-hidden="true"><span className="switch__thumb" /></span>
                  <span className="switch__label">{t('room.pauseBetweenHands')}</span>
                </label>

                <h3 className="field__label">{t('room.schedule')}</h3>
                {/* §10 — the invite renders a start time in each recipient's own
                    timezone. A native datetime input is not a control we can
                    make look like anything, so it only appears once scheduling
                    is actually chosen. */}
                <Segmented
                  label={t('room.schedule')}
                  value={room.config.startAt ? 'later' : 'now'}
                  disabled={!isHost}
                  options={[
                    { value: 'now', label: t('room.playNow') },
                    { value: 'later', label: t('room.playLater') },
                  ]}
                  onChange={(when) =>
                    set({ startAt: when === 'now' ? null : Date.now() + 3_600_000 })
                  }
                />
                {room.config.startAt !== null && (
                  <>
                    <input
                      type="datetime-local"
                      className="field__input field__input--time"
                      disabled={!isHost}
                      value={toLocalInput(room.config.startAt)}
                      onChange={(e) =>
                        set({
                          startAt: e.target.value
                            ? new Date(e.target.value).getTime()
                            : Date.now() + 3_600_000,
                        })
                      }
                    />
                    <p className="muted muted--small">
                      {t('room.startsAt', { at: room.config.startAt })}
                    </p>
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="room__bar">
        <button
          type="button"
          className="btn btn--ghost room__primer-button"
          onClick={() => {
            setPrimerStep(0);
            setPrimerOpen(true);
          }}
        >
          {t('room.primer.open')}
        </button>
        <span className="room__bar-status">
          {canStart ? (
            <span className="room__ready">{t('room.ready')}</span>
          ) : (
            <span className="muted">{t('room.start.hint')}</span>
          )}
          {!isHost && canStart && (
            <span className="muted muted--small">{t('table.waitingForHost')}</span>
          )}
        </span>
        <button
          type="button"
          className="btn btn--primary btn--large room__start-button"
          disabled={!isHost || !canStart}
          onClick={() => client.startSession()}
        >
          {t('room.start')}
        </button>
        {isHost && room.config.startAt === null && (
          <button
            type="button"
            className="btn btn--ghost room__countdown-button"
            disabled={!canStart}
            onClick={() => {
              set({ startAt: Date.now() + 15_000 });
              client.startSession();
            }}
          >
            {t('room.startCountdown')}
          </button>
        )}
      </div>

      {primerOpen && (
        <RulePrimer
          step={primerStep}
          onStep={setPrimerStep}
          onClose={() => setPrimerOpen(false)}
        />
      )}
    </main>
  );
}

function toLocalInput(epoch: number): string {
  const d = new Date(epoch - new Date().getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}
